import React, { useState, useRef } from 'react';
import { getAuthToken } from '../api/api';
import { Upload, AlertTriangle, CheckCircle, FileArchive, Database, Shield, Loader2, X, Search, RotateCcw, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RestaurarBackup = () => {
  const [archivo, setArchivo] = useState(null);
  const [comparando, setComparando] = useState(false);
  const [restaurando, setRestaurando] = useState(false);
  const [comparacion, setComparacion] = useState(null);
  const [selFaltantes, setSelFaltantes] = useState({});
  const [selModificados, setSelModificados] = useState({});
  const [selAfectados, setSelAfectados] = useState([]);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);
  const [expandidos, setExpandidos] = useState({});
  const inputRef = useRef(null);

  const token = getAuthToken();

  const handleArchivoChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith('.zip')) {
      setArchivo(file);
      setError(null);
      setResultado(null);
      setComparacion(null);
      setSelFaltantes({});
      setSelModificados({});
      setSelAfectados([]);
    } else if (file) {
      setError('Solo se aceptan archivos .zip');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith('.zip')) {
      setArchivo(file);
      setError(null);
      setResultado(null);
      setComparacion(null);
      setSelFaltantes({});
      setSelModificados({});
      setSelAfectados([]);
    } else {
      setError('Solo se aceptan archivos .zip');
    }
  };

  const compararBackup = async () => {
    if (!archivo) return;
    setComparando(true);
    setError(null);
    setComparacion(null);
    setSelFaltantes({});
    setSelModificados({});
    setSelAfectados([]);

    try {
      const formData = new FormData();
      formData.append('archivo', archivo);

      const res = await fetch(`${API_URL}/api/cobros-v3/admin/backup/comparar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      const data = await res.json();
      if (res.ok) {
        setComparacion(data);
        const preFalt = {};
        const preMod = {};
        data.colecciones.forEach((col) => {
          if (col.faltantes?.length > 0) preFalt[col.nombre] = col.faltantes.map((f) => f.id);
          if (col.modificados?.length > 0) preMod[col.nombre] = [];
        });
        setSelFaltantes(preFalt);
        setSelModificados(preMod);
        // Pre-select all affected platos
        if (data.platos_afectados?.length > 0) {
          setSelAfectados(data.platos_afectados.map((p) => p.id));
        }
      } else {
        setError(data.detail || 'Error al comparar backup');
      }
    } catch (err) {
      setError('Error de conexión: ' + err.message);
    } finally {
      setComparando(false);
    }
  };

  const toggleSel = (setter, coleccion, id) => {
    setter((prev) => {
      const current = prev[coleccion] || [];
      if (current.includes(id)) return { ...prev, [coleccion]: current.filter((i) => i !== id) };
      return { ...prev, [coleccion]: [...current, id] };
    });
  };

  const toggleTodos = (setter, coleccion, ids) => {
    setter((prev) => {
      const current = prev[coleccion] || [];
      if (current.length === ids.length) return { ...prev, [coleccion]: [] };
      return { ...prev, [coleccion]: [...ids] };
    });
  };

  const totalFalt = Object.values(selFaltantes).reduce((s, a) => s + a.length, 0);
  const totalMod = Object.values(selModificados).reduce((s, a) => s + a.length, 0);
  const totalAfect = selAfectados.length;
  const totalSel = totalFalt + totalMod + totalAfect;

  const restaurarSeleccionados = async () => {
    if (totalSel === 0) return;
    setRestaurando(true);
    setError(null);

    try {
      const items = {};
      const sobrescribir = {};

      comparacion.colecciones.forEach((col) => {
        const fIds = selFaltantes[col.nombre] || [];
        if (fIds.length > 0) {
          items[col.nombre] = col.faltantes
            .filter((f) => fIds.includes(f.id))
            .map((f) => ({ id: f.id, datos: f.datos }));
        }
        const mIds = selModificados[col.nombre] || [];
        if (mIds.length > 0) {
          sobrescribir[col.nombre] = col.modificados
            .filter((m) => mIds.includes(m.id))
            .map((m) => ({ id: m.id, datos: m.datos }));
        }
      });

      // Add affected platos to overwrite
      if (selAfectados.length > 0 && comparacion.platos_afectados) {
        const platosToOverwrite = comparacion.platos_afectados
          .filter((p) => selAfectados.includes(p.id))
          .map((p) => ({ id: p.id, datos: p.datos }));
        if (platosToOverwrite.length > 0) {
          sobrescribir['platos'] = [...(sobrescribir['platos'] || []), ...platosToOverwrite];
        }
      }

      const res = await fetch(`${API_URL}/api/cobros-v3/admin/backup/restaurar-selectivo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, sobrescribir }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setResultado(data);
        setComparacion(null);
        setSelFaltantes({});
        setSelModificados({});
        setSelAfectados([]);
      } else {
        setError(data.detail || 'Error al restaurar');
      }
    } catch (err) {
      setError('Error de conexión: ' + err.message);
    } finally {
      setRestaurando(false);
    }
  };

  const toggleExp = (key) => setExpandidos((p) => ({ ...p, [key]: !p[key] }));

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const CAMPO_LEGIBLE = {
    ingredientes: 'ingredientes', ingredientes_ids: 'ingredientes',
    nombre: 'nombre', alergenos: 'alérgenos', trazas_alergenos: 'trazas',
    tipo: 'tipo', platos_ids: 'platos', secciones: 'secciones',
    precio_unitario: 'precio', alergenos_ids: 'alérgenos',
    activo: 'estado', stock: 'stock', ubicacion: 'ubicación',
  };

  const hasDifferences = comparacion && (comparacion.total_faltantes > 0 || comparacion.total_modificados > 0 || comparacion.platos_afectados?.length > 0);

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Database className="w-7 h-7 text-duetto-primary" />
          Restaurar Backup
        </h1>
        <p className="text-gray-500 mt-1">Compara tu backup con la base de datos y restaura solo lo que necesites</p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start gap-3">
        <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-semibold text-blue-800">Restauración selectiva segura</p>
          <p className="text-sm text-blue-700 mt-1">
            El sistema detecta registros <strong>eliminados</strong> (faltantes) y registros <strong>modificados</strong> (que cambiaron respecto al backup).
            Puedes elegir exactamente qué restaurar sin afectar datos nuevos.
          </p>
        </div>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all ${
          archivo ? 'border-green-300 bg-green-50' : 'border-gray-300 bg-gray-50 hover:border-duetto-primary hover:bg-duetto-primary/5'
        }`}
        data-testid="dropzone-backup"
      >
        <input ref={inputRef} type="file" accept=".zip" onChange={handleArchivoChange} className="hidden" data-testid="input-backup-file" />
        {archivo ? (
          <div className="flex items-center justify-center gap-3">
            <FileArchive className="w-10 h-10 text-green-600" />
            <div className="text-left">
              <p className="font-semibold text-gray-900">{archivo.name}</p>
              <p className="text-sm text-gray-500">{formatBytes(archivo.size)}</p>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); setArchivo(null); setComparacion(null); setSelFaltantes({}); setSelModificados({}); setResultado(null); if (inputRef.current) inputRef.current.value = ''; }}
              className="ml-4 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <>
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="font-medium text-gray-700">Arrastra el archivo ZIP aquí o haz clic para seleccionar</p>
            <p className="text-sm text-gray-400 mt-1">Solo archivos .zip generados por el sistema de backup</p>
          </>
        )}
      </div>

      {archivo && !comparacion && !resultado && (
        <button
          onClick={compararBackup}
          disabled={comparando}
          className="mt-4 w-full py-3 px-4 bg-duetto-primary hover:bg-duetto-primary/90 disabled:bg-gray-300 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
          data-testid="btn-comparar"
        >
          {comparando ? <><Loader2 className="w-5 h-5 animate-spin" /> Analizando backup...</> : <><Search className="w-5 h-5" /> Comparar con base de datos actual</>}
        </button>
      )}

      {/* Results */}
      {comparacion && (
        <div className="mt-6 space-y-4">
          {!hasDifferences ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-5 flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 mt-0.5" />
              <div>
                <p className="font-semibold text-green-800">La base de datos está completa</p>
                <p className="text-sm text-green-700 mt-1">No hay registros faltantes ni modificados.</p>
              </div>
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-amber-800">
                    {comparacion.total_faltantes > 0 && `${comparacion.total_faltantes} eliminados`}
                    {comparacion.total_faltantes > 0 && comparacion.total_modificados > 0 && ' y '}
                    {comparacion.total_modificados > 0 && `${comparacion.total_modificados} modificados`}
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    Selecciona los registros que quieras restaurar. Los datos nuevos que no están en el backup NO se tocarán.
                  </p>
                </div>
              </div>

              {comparacion.colecciones
                .filter((col) => (col.faltantes?.length > 0) || (col.modificados?.length > 0))
                .map((col) => {
                  const fIds = col.faltantes?.map((f) => f.id) || [];
                  const mIds = col.modificados?.map((m) => m.id) || [];
                  const selF = selFaltantes[col.nombre] || [];
                  const selM = selModificados[col.nombre] || [];
                  const hasFalt = fIds.length > 0;
                  const hasMod = mIds.length > 0;

                  return (
                    <div key={col.nombre} className="border border-gray-200 rounded-xl overflow-hidden" data-testid={`col-${col.nombre}`}>
                      {/* Header */}
                      <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
                        <div>
                          <span className="font-semibold text-gray-900">{col.nombre_legible}</span>
                          {hasFalt && <span className="ml-2 text-sm text-red-600 font-medium">{fIds.length} eliminados</span>}
                          {hasMod && <span className="ml-2 text-sm text-amber-600 font-medium">{mIds.length} modificados</span>}
                          <span className="ml-2 text-xs text-gray-400">(backup: {col.en_backup} | BD: {col.en_bd})</span>
                        </div>
                      </div>

                      {/* Faltantes */}
                      {hasFalt && (
                        <div className="border-t border-gray-100">
                          <div
                            className="px-4 py-2 bg-red-50/50 flex items-center justify-between cursor-pointer hover:bg-red-50"
                            onClick={() => toggleExp(`${col.nombre}_falt`)}
                          >
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={selF.length === fIds.length && fIds.length > 0}
                                onChange={(e) => { e.stopPropagation(); toggleTodos(setSelFaltantes, col.nombre, fIds); }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-4 h-4 rounded border-gray-300 text-red-600"
                              />
                              <span className="text-sm font-medium text-red-700">Eliminados ({fIds.length})</span>
                              <span className="text-xs text-red-500">— se insertarán de nuevo</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{selF.length}/{fIds.length}</span>
                              {expandidos[`${col.nombre}_falt`] === false ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronUp className="w-4 h-4 text-gray-400" />}
                            </div>
                          </div>
                          {expandidos[`${col.nombre}_falt`] !== false && (
                            <div className="divide-y divide-gray-50 max-h-60 overflow-y-auto">
                              {col.faltantes.map((item) => (
                                <label key={item.id} className="flex items-center gap-3 px-6 py-2 hover:bg-gray-50 cursor-pointer">
                                  <input type="checkbox" checked={selF.includes(item.id)} onChange={() => toggleSel(setSelFaltantes, col.nombre, item.id)} className="w-4 h-4 rounded border-gray-300 text-red-600 flex-shrink-0" />
                                  <span className="text-sm text-gray-800">{item.nombre || item.id}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Modificados */}
                      {hasMod && (
                        <div className="border-t border-gray-100">
                          <div
                            className="px-4 py-2 bg-amber-50/50 flex items-center justify-between cursor-pointer hover:bg-amber-50"
                            onClick={() => toggleExp(`${col.nombre}_mod`)}
                          >
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={selM.length === mIds.length && mIds.length > 0}
                                onChange={(e) => { e.stopPropagation(); toggleTodos(setSelModificados, col.nombre, mIds); }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-4 h-4 rounded border-gray-300 text-amber-600"
                              />
                              <span className="text-sm font-medium text-amber-700">Modificados ({mIds.length})</span>
                              <span className="text-xs text-amber-500">— se sobrescribirán con la versión del backup</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{selM.length}/{mIds.length}</span>
                              {expandidos[`${col.nombre}_mod`] === false ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronUp className="w-4 h-4 text-gray-400" />}
                            </div>
                          </div>
                          {expandidos[`${col.nombre}_mod`] !== false && (
                            <div className="divide-y divide-gray-50 max-h-60 overflow-y-auto">
                              {col.modificados.map((item) => (
                                <label key={item.id} className="flex items-center gap-3 px-6 py-2 hover:bg-gray-50 cursor-pointer">
                                  <input type="checkbox" checked={selM.includes(item.id)} onChange={() => toggleSel(setSelModificados, col.nombre, item.id)} className="w-4 h-4 rounded border-gray-300 text-amber-600 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <span className="text-sm text-gray-800">{item.nombre || item.id}</span>
                                    <div className="flex gap-1 mt-0.5 flex-wrap">
                                      {item.campos_cambiados?.map((c) => (
                                        <span key={c} className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                                          {CAMPO_LEGIBLE[c] || c}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

              {/* Affected platos section */}
              {comparacion.platos_afectados?.length > 0 && (
                <div className="border-2 border-purple-200 rounded-xl overflow-hidden bg-purple-50/30" data-testid="platos-afectados">
                  <div
                    className="px-4 py-3 bg-purple-50 flex items-center justify-between cursor-pointer hover:bg-purple-100"
                    onClick={() => toggleExp('platos_afectados')}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selAfectados.length === comparacion.platos_afectados.length}
                        onChange={(e) => {
                          e.stopPropagation();
                          setSelAfectados(selAfectados.length === comparacion.platos_afectados.length ? [] : comparacion.platos_afectados.map((p) => p.id));
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded border-gray-300 text-purple-600"
                      />
                      <div>
                        <span className="font-semibold text-purple-900">Platos afectados por ingredientes eliminados</span>
                        <span className="ml-2 text-sm text-purple-600 font-medium">{comparacion.platos_afectados.length} platos</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-purple-200 text-purple-800 px-2 py-0.5 rounded-full">
                        {selAfectados.length}/{comparacion.platos_afectados.length}
                      </span>
                      {expandidos['platos_afectados'] === false ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronUp className="w-4 h-4 text-gray-400" />}
                    </div>
                  </div>

                  <div className="px-4 py-2 bg-purple-50/50 border-t border-purple-100">
                    <p className="text-xs text-purple-700">
                      Estos platos usaban ingredientes que se eliminaron. Al sobrescribirlos con la versión del backup, se restaurará su configuración original con todos sus ingredientes.
                    </p>
                  </div>

                  {expandidos['platos_afectados'] !== false && (
                    <div className="divide-y divide-purple-50 max-h-80 overflow-y-auto bg-white">
                      {comparacion.platos_afectados.map((plato) => (
                        <label key={plato.id} className="flex items-start gap-3 px-6 py-2.5 hover:bg-purple-50/30 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selAfectados.includes(plato.id)}
                            onChange={() => {
                              setSelAfectados((prev) =>
                                prev.includes(plato.id) ? prev.filter((id) => id !== plato.id) : [...prev, plato.id]
                              );
                            }}
                            className="w-4 h-4 rounded border-gray-300 text-purple-600 mt-0.5 flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-gray-800">{plato.nombre}</span>
                            <div className="flex gap-1 mt-1 flex-wrap">
                              {plato.ingredientes_perdidos?.map((ing, i) => (
                                <span key={i} className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                                  {ing}
                                </span>
                              ))}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Restore button */}
              <button
                onClick={restaurarSeleccionados}
                disabled={totalSel === 0 || restaurando}
                className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                data-testid="btn-restaurar-selectivo"
              >
                {restaurando ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Restaurando...</>
                ) : (
                  <><RotateCcw className="w-5 h-5" /> Restaurar {totalSel} registros ({totalFalt > 0 ? `${totalFalt} insertar` : ''}{totalFalt > 0 && (totalMod + totalAfect) > 0 ? ', ' : ''}{(totalMod + totalAfect) > 0 ? `${totalMod + totalAfect} sobrescribir` : ''})</>
                )}
              </button>
            </>
          )}
        </div>
      )}

      {error && (
        <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3" data-testid="backup-error">
          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {resultado && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-5" data-testid="backup-result">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-6 h-6 text-green-600 mt-0.5" />
            <div>
              <p className="font-semibold text-green-800 text-lg">{resultado.mensaje}</p>
              {resultado.resultados?.detalle?.map((d, i) => (
                <p key={i} className="text-sm text-green-700 mt-1">
                  {d.coleccion}: {d.cantidad} registros {d.accion === 'sobrescribir' ? 'sobrescritos' : 'restaurados'}
                </p>
              ))}
              {resultado.resultados?.errores?.length > 0 && (
                <div className="mt-2 text-sm text-amber-700">
                  <p className="font-medium">Errores:</p>
                  {resultado.resultados.errores.map((e, i) => <p key={i}>{e}</p>)}
                </div>
              )}
              <p className="text-xs text-green-600 mt-3">Esta acción ha quedado registrada en el log de auditoría.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurarBackup;
